import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import HeroCarousel from '@/pages/home/components/HeroCarousel';
import VideoCard from '@/components/feature/VideoCard';
import { categories } from '@/mocks/categories';
import { videos } from '@/mocks/videos';
import type { Video } from '@/types/video';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

export default function Home() {
  const [tab, setTab] = useState<'hot' | 'new'>('hot');

  useDocumentTitle({
    title: '成人快手 - 海量成人人群短视频娱乐平台',
    description:
      '成人快手为广大用户提供丰富多样的成人主题短视频内容，涵盖生活娱乐、兴趣爱好等多个领域，支持视频浏览和互动交流。',
    keywords: '成人快手,成人短视频,趣味视频,生活分享,娱乐短视频,成人人群,短视频平台',
  });
  const [uploadOpen, setUploadOpen] = useState(false);

  const allVideos = videos as Video[];

  const sorted = useMemo(() => {
    const list = [...allVideos];
    if (tab === 'hot') {
      return list.sort((a, b) => b.views - a.views).slice(0, 8);
    }
    return list
      .sort((a, b) => new Date(b.publishDate).getTime() - new Date(a.publishDate).getTime())
      .slice(0, 8);
  }, [tab, allVideos]);

  const countByCategory = useMemo(() => {
    const map: Record<string, number> = {};
    allVideos.forEach((v) => {
      map[v.category] = (map[v.category] || 0) + 1;
    });
    return map;
  }, [allVideos]);

  return (
    <>
      <HeroCarousel videos={allVideos} />

      {/* Category exploration */}
      <section className="w-full px-4 py-12 md:px-8 md:py-16" id="category">
        <div className="mb-6 flex items-end justify-between">
          <div>
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">分类探索</h2>
            <p className="mt-1 text-sm text-foreground-500">按兴趣快速找到你想看的内容</p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">
          {categories.map((c) => (
            <Link
              key={c.id}
              to={`/category/${c.id}`}
              className="group flex items-center gap-3 rounded-lg border border-background-200/70 bg-background-100 p-4 transition-colors hover:border-primary-700 hover:bg-background-200/60"
            >
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-secondary-500/15 text-secondary-300 transition-colors group-hover:bg-primary-500/20 group-hover:text-primary-300">
                <i className={`${c.icon} text-xl`} />
              </span>
              <div className="min-w-0">
                <p className="font-heading text-sm font-semibold text-foreground-950">{c.name}</p>
                <p className="truncate text-xs text-foreground-500">
                  {countByCategory[c.id] || 0} 个视频
                </p>
              </div>
              <i className="ri-arrow-right-s-line ml-auto text-foreground-400 transition-transform group-hover:translate-x-1 group-hover:text-primary-300" />
            </Link>
          ))}
        </div>
      </section>

      {/* Hot / new recommendations */}
      <section className="w-full bg-background-100/60 px-4 py-12 md:px-8 md:py-16">
        <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
          <div>
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              {tab === 'hot' ? '热门视频推荐' : '最新上传'}
            </h2>
            <p className="mt-1 text-sm text-foreground-500">精选成年用户正在观看的短视频</p>
          </div>
          <div className="flex rounded-full border border-background-300/60 bg-background-50 p-1">
            <button
              type="button"
              onClick={() => setTab('hot')}
              className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                tab === 'hot' ? 'bg-primary-500 text-foreground-950' : 'text-foreground-600 hover:text-foreground-950'
              }`}
            >
              最热
            </button>
            <button
              type="button"
              onClick={() => setTab('new')}
              className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                tab === 'new' ? 'bg-primary-500 text-foreground-950' : 'text-foreground-600 hover:text-foreground-950'
              }`}
            >
              最新
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {sorted.map((v) => (
            <VideoCard key={v.id} video={v} />
          ))}
        </div>

        <div className="mt-8 flex justify-center">
          <Link
            to="/category/life"
            className="flex items-center gap-2 rounded-full border border-background-300/70 px-6 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-600 hover:text-primary-300"
          >
            浏览更多分类内容
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </section>

      {/* Upload entry CTA */}
      <section className="w-full px-4 py-12 md:px-8 md:py-16">
        <div className="relative w-full overflow-hidden rounded-xl border border-primary-800/60 bg-primary-950 p-6 md:p-10">
          <div className="relative z-10 flex flex-col items-start justify-between gap-6 md:flex-row md:items-center">
            <div className="max-w-xl">
              <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
                分享你的短视频，让更多人看到
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-foreground-700">
                上传属于你的生活片段、才艺或创意，与兴趣相投的成年用户一起交流互动。
              </p>
            </div>
            <button
              type="button"
              onClick={() => setUploadOpen(true)}
              className="flex shrink-0 items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-semibold text-foreground-950 transition-colors hover:bg-primary-400"
            >
              <i className="ri-upload-cloud-2-line text-lg" />
              新增视频
            </button>
          </div>
          <i className="ri-video-upload-line absolute -right-6 -top-6 text-[140px] text-primary-700/30" />
        </div>
      </section>

      {uploadOpen && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 p-4"
          onClick={() => setUploadOpen(false)}
        >
          <div
            className="w-full max-w-md animate-fade-up rounded-xl border border-background-200 bg-background-100 p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-heading text-lg font-bold text-foreground-950">新增视频</h3>
              <button
                type="button"
                onClick={() => setUploadOpen(false)}
                className="flex h-8 w-8 items-center justify-center rounded-full bg-background-200 text-foreground-700"
                aria-label="关闭"
              >
                <i className="ri-close-line" />
              </button>
            </div>
            <div className="flex flex-col items-center rounded-lg border border-dashed border-background-300 bg-background-50 py-8 text-center">
              <span className="mb-3 flex h-14 w-14 items-center justify-center rounded-full bg-primary-500/15 text-primary-300">
                <i className="ri-add-line text-2xl" />
              </span>
              <p className="text-sm font-medium text-foreground-900">上传功能为演示入口</p>
              <p className="mt-2 max-w-xs text-xs leading-relaxed text-foreground-500">
                当前为前端演示站点，暂未接入真实上传接口。你可以先浏览与收藏站内精选内容。
              </p>
            </div>
            <button
              type="button"
              onClick={() => setUploadOpen(false)}
              className="mt-5 w-full rounded-full bg-primary-500 py-3 text-sm font-semibold text-foreground-950 transition-colors hover:bg-primary-400"
            >
              我知道了
            </button>
          </div>
        </div>
      )}
    </>
  );
}